import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import {
  Box,
  Button,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Autocomplete,
  FormControlLabel,
  Typography,
  RadioGroup,
  Radio,
  Divider,
} from "@mui/material";
import { useAssessmentForm } from "../hooks/useAssessmentForm";
import { debounce } from "lodash";
import { searchUsers } from "reduxStore/actions/userActions";
import { useDispatch } from "react-redux";

function AssessmentForm({ editMode, currentAssessment, setOpen, handleClose }) {
  const {
    validationSchema,
    initialValues,
    handleSubmit,
    setSearchDivisionTerm,
    divisionSearchResults,
    principleQuestions,
    setValuesLocation,
    levelsForLocation,
  } = useAssessmentForm({
    editMode,
    currentAssessment,
    setOpen,
  });
  const dispatch = useDispatch();

  // New component to handle side-effects
  const LocationWatcher = ({ setValuesLocation }) => {
    const { values } = useFormikContext();

    useEffect(() => {
      if (values.location) {
        setValuesLocation(values.location);
      }
    }, [values.location, setValuesLocation]);

    return null; // This component doesn't render anything
  };

  const groupedPrincipleQuestions = principleQuestions.reduce(
    (acc, question) => {
      // If question.principle is not an object, default to a fallback name.
      const principleName =
        (question.principle && question.principle.name) || "Other";
      if (!acc[principleName]) {
        acc[principleName] = [];
      }
      acc[principleName].push(question);
      return acc;
    },
    {}
  );

  const getQuestionText = (q) => {
    if (!q) return "";
    if (typeof q === "object") {
      // If the object has a 'question' property and it is a string, return it.
      if ("question" in q) {
        const text = q.question;
        return typeof text === "string" ? text : String(text);
      }
      // Otherwise, return a JSON string as fallback.
      return JSON.stringify(q);
    }
    return String(q);
  };

  // --- New: a free-solo Autocomplete for “assessee” + name ---
  function AssesseeField({ values, setFieldValue, touched, errors }) {
    const [options, setOptions] = useState([]);

    // debounce your dispatch(searchUsers)
    const fetchUsers = useMemo(() => {
      // dispatch is stable from redux; no deps needed
      return debounce((term) => {
        if (term && term.length >= 2) {
          dispatch(searchUsers(term)).then((res) => {
            // map each user to include a fullName
            const users = (res || []).map((u) => ({
              ...u,
              fullName: `${u.firstName} ${u.lastName}`,
            }));
            setOptions(users);
          });
        }
      }, 300);
    }, []);

    return (
      <Autocomplete
        freeSolo
        options={options}
        getOptionLabel={(opt) => {
          if (typeof opt === "string") return opt;
          const name =
            opt.fullName ||
            (opt.firstName || opt.lastName
              ? `${opt.firstName || ""} ${opt.lastName || ""}`.trim()
              : "");
          if (!name) return "";
          return opt.email ? `${name} (${opt.email})` : name;
        }}
        // When the user types:
        onInputChange={(_, input, reason) => {
          if (reason === "reset") return;
          // Keep changes tied to user input or explicit clears.
          if (reason === "clear") {
            setFieldValue("name", "");
            setFieldValue("assessee", null);
            setFieldValue("assesseeEmail", "");
            return;
          }
          // Always update the “name” string for manual typing.
          setFieldValue("name", input);
          // If they’re free‐typing, drop any previous linkage.
          setFieldValue("assessee", null);
          setFieldValue("assesseeEmail", "");
          fetchUsers(input);
        }}
        // When they pick an existing user:
        onChange={(_, value) => {
          if (value && typeof value !== "string") {
            setFieldValue("name", value.fullName);
            setFieldValue("assessee", value._id);
            setFieldValue("assesseeEmail", value.email || "");
          } else if (!value) {
            setFieldValue("assessee", null);
            setFieldValue("assesseeEmail", "");
          }
        }}
        // Display either the linked user object or the raw name
        value={
          values.assessee
            ? options.find((u) => u._id === values.assessee) || {
                _id: values.assessee,
                fullName: values.name,
                email: values.assesseeEmail,
              }
            : values.name
        }
        renderOption={(props, option) => {
          const displayName =
            typeof option === "string"
              ? option
              : `${option.firstName} ${option.lastName}`;
          const email = typeof option === "string" ? "" : option.email;
          return (
            <li
              {...props}
              key={typeof option === "string" ? option : option._id}
            >
              <Box display="flex" flexDirection="column">
                <span>{displayName}</span>
                {email && (
                  <Typography variant="caption" color="text.secondary">
                    {email}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Assessee’s Name or Email"
            variant="outlined"
            margin="normal"
            fullWidth
            // wire up Formik’s touched/errors for “name”
            error={Boolean(touched.name && errors.name)}
            helperText={touched.name && errors.name}
          />
        )}
      />
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={Boolean(editMode)}
    >
      {({
        isSubmitting,
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        getFieldProps,
        setFieldValue,
      }) => {
        return (
          <Form>
            {/* This watches for changes in the location */}
            <LocationWatcher setValuesLocation={setValuesLocation} />
            <Box sx={{ p: 3 }}>
              {/* Assessment Name */}
              <AssesseeField
                values={values}
                setFieldValue={setFieldValue}
                touched={touched}
                errors={errors}
              />

              {!values.assessee && (
                <TextField
                  fullWidth
                  id="assesseeEmail"
                  name="assesseeEmail"
                  label="Assessee’s Email"
                  type="email"
                  variant="outlined"
                  margin="normal"
                  value={values.assesseeEmail}
                  onChange={(e) => {
                    setFieldValue("assesseeEmail", e.target.value);
                  }}
                  onBlur={handleBlur}
                  error={touched.assesseeEmail && Boolean(errors.assesseeEmail)}
                  helperText={
                    touched.assesseeEmail
                      ? errors.assesseeEmail
                      : "No match? Add their email to create/link the teacher."
                  }
                />
              )}

              {/* Date of Assessment */}
              <TextField
                fullWidth
                type="date"
                id="dateOfAssessment"
                name="dateOfAssessment"
                label="Date of Assessment"
                variant="outlined"
                margin="normal"
                InputLabelProps={{ shrink: true }}
                {...getFieldProps("dateOfAssessment")}
                error={
                  touched.dateOfAssessment && Boolean(errors.dateOfAssessment)
                }
                helperText={touched.dateOfAssessment && errors.dateOfAssessment}
              />

              {/* Lesson Period */}
              <FormControl fullWidth margin="normal">
                <InputLabel>Lesson Period</InputLabel>
                <Select
                  id="lessonPeriod"
                  name="lessonPeriod"
                  value={values.lessonPeriod}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.lessonPeriod && Boolean(errors.lessonPeriod)}
                >
                  <MenuItem value="Morning">Morning</MenuItem>
                  <MenuItem value="Afternoon">Afternoon</MenuItem>
                  {[...Array(6).keys()].map((num) => (
                    <MenuItem key={num + 1} value={`Period ${num + 1}`}>
                      Period {num + 1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Division */}
              <Autocomplete
                options={
                  // Merge the current division (if any) with the available options.
                  // Using a Set (or filtering duplicates) can ensure we only have unique items.
                  [
                    ...(divisionSearchResults || []),
                    ...(values.division ? [values.division] : []),
                  ]
                }
                getOptionLabel={(option) =>
                  option?.name && option?.location
                    ? `${option.name} - ${option.location.name}`
                    : ""
                }
                noOptionsText="No departments found"
                value={values.division || null}
                isOptionEqualToValue={(option, value) =>
                  option?._id?.toString() === value?._id?.toString()
                }
                onInputChange={(event, newInputValue) => {
                  setSearchDivisionTerm(newInputValue);
                }}
                onChange={(event, newValue) => {
                  setFieldValue("division", newValue);
                  setFieldValue("location", newValue?.location || null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.division && Boolean(errors.division)}
                    helperText={touched.division && errors.division}
                  />
                )}
              />

              {/* Levels */}

              <Autocomplete
                options={levelsForLocation || []}
                getOptionLabel={(option) => option.name}
                // noOptionsText="No year found"
                value={
                  values.level && Object.keys(values.level).length > 0
                    ? values.level
                    : null
                }
                isOptionEqualToValue={(option, value) =>
                  option?._id?.toString() === value?._id?.toString()
                }
                onChange={(event, newValue) => {
                  setFieldValue("level", newValue);
                }}
                disabled={!values.division?._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Year"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.level && Boolean(errors.level)}
                    helperText={touched.level && errors.level}
                  />
                )}
              />

              {/* Display Principle Questions with Radio Buttons */}
              {values.division?._id && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6">T&L Foci</Typography>
                  {Object.entries(groupedPrincipleQuestions).map(
                    ([principleName, questions]) => (
                      <Box key={principleName} sx={{ mt: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold" }}
                        >
                          {principleName}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        {questions.map((question) => {
                          const selected = values.principleQuestions.find(
                            (pq) =>
                              (typeof pq.question === "object"
                                ? pq.question._id
                                : pq.question) === question._id
                          );
                          return (
                            <Box
                              key={question._id}
                              sx={{
                                mb: 2,
                                p: 2,
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                              }}
                            >
                              <Typography>
                                {getQuestionText(question.question)}
                              </Typography>

                              <RadioGroup
                                row
                                value={
                                  selected
                                    ? selected.areaOfStrength
                                      ? "strength"
                                      : "development"
                                    : ""
                                }
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value) {
                                    const updatedQuestions =
                                      values.principleQuestions.filter(
                                        (pq) =>
                                          (typeof pq.question === "object"
                                            ? pq.question._id
                                            : pq.question) !== question._id
                                      );
                                    setFieldValue("principleQuestions", [
                                      ...updatedQuestions,
                                      {
                                        question: question._id,
                                        principle: question.principle._id,
                                        answer: "",
                                        areaOfStrength: value === "strength",
                                      },
                                    ]);
                                  } else {
                                    setFieldValue(
                                      "principleQuestions",
                                      values.principleQuestions.filter(
                                        (pq) =>
                                          (typeof pq.question === "object"
                                            ? pq.question._id
                                            : pq.question) !== question._id
                                      )
                                    );
                                  }
                                }}
                              >
                                <FormControlLabel
                                  value="strength"
                                  control={<Radio />}
                                  label="Area of Strength"
                                />
                                <FormControlLabel
                                  value="development"
                                  control={<Radio />}
                                  label="Area for Development"
                                />
                                <FormControlLabel
                                  value=""
                                  control={<Radio />}
                                  label="None"
                                />
                              </RadioGroup>
                            </Box>
                          );
                        })}
                      </Box>
                    )
                  )}
                </Box>
              )}

              {errors.principleQuestions && (
                <Typography color="error" sx={{ mt: 1 }}>
                  {typeof errors.principleQuestions === "string"
                    ? errors.principleQuestions
                    : JSON.stringify(errors.principleQuestions)}
                </Typography>
              )}

              {/* Notes */}
              <TextField
                fullWidth
                id="notes"
                name="notes"
                label="Notes"
                variant="outlined"
                margin="normal"
                multiline
                rows={3}
                {...getFieldProps("notes")}
                error={touched.notes && Boolean(errors.notes)}
                helperText={touched.notes && errors.notes}
              />
            </Box>

            {/* Dialog Actions */}
            <DialogActions>
              <Button onClick={handleClose} color="secondary">
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={isSubmitting}>
                {editMode ? "Update Assessment" : "Create Assessment"}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
}

export default AssessmentForm;
